import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { artistsAPI, videosAPI, adminVideosAPI, youtubeAdminAPI, adminArtistsAPI, adminUsersAPI } from '../services/api';
import AlertModal from '../components/common/AlertModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { resizeImageToDataUrl } from '../utils/imageResize';

const ArtistManagement = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [artists, setArtists] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistVideos, setArtistVideos] = useState([]);

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [duplicateArtist, setDuplicateArtist] = useState(null);
  const [keepArtist, setKeepArtist] = useState(null);
  const [mergeSearchQuery, setMergeSearchQuery] = useState('');
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [merging, setMerging] = useState(false);

  // Reassign state
  const [reassignVideo, setReassignVideo] = useState(null);
  const [reassignOldArtist, setReassignOldArtist] = useState(null);
  const [reassignSearchQuery, setReassignSearchQuery] = useState('');
  const [reassignSearchResults, setReassignSearchResults] = useState([]);
  const [reassigning, setReassigning] = useState(false);

  // Create rapper state
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  // Convert user to rapper state
  const [showConvertPanel, setShowConvertPanel] = useState(false);
  const [convertUserSearch, setConvertUserSearch] = useState('');
  const [convertUserResults, setConvertUserResults] = useState([]);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertPlaceholderSearch, setConvertPlaceholderSearch] = useState('');
  const [convertPlaceholderResults, setConvertPlaceholderResults] = useState([]);
  const [convertPlaceholder, setConvertPlaceholder] = useState(null);
  const [converting, setConverting] = useState(false);

  // Avatar upload state (admin uploads avatar for an artist)
  const [avatarUploadingId, setAvatarUploadingId] = useState(null);
  const avatarFileInputRef = useRef(null);
  const [pendingAvatarArtistId, setPendingAvatarArtistId] = useState(null);

  // Modals
  const [alertModal, setAlertModal] = useState({
    show: false, title: '', message: '', type: 'info',
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false, title: '', message: '', onConfirm: null,
  });

  useEffect(() => {
    if (user && isAdmin()) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [artistsRes, videosRes] = await Promise.all([
        artistsAPI.getAll(),
        videosAPI.getAll({ per_page: 5000, include_unlisted: true }), // admin mgmt: count unlisted too
      ]);

      const allVideos = videosRes.data;
      setVideos(allVideos);

      // Calculate video count for each artist
      const artistsWithCount = artistsRes.data.map(artist => {
        const videoCount = allVideos.filter(video =>
          video.artists?.some(a => a.id === artist.id)
        ).length;
        return { ...artist, video_count: videoCount };
      });

      setArtists(artistsWithCount);
    } catch (error) {
      console.error('Error fetching data:', error);
      setAlertModal({
        show: true, title: 'Error',
        message: 'Failed to load artists and videos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtered + sorted artists
  const filteredArtists = useMemo(() => {
    let result = artists;
    if (searchFilter.trim()) {
      const query = searchFilter.toLowerCase();
      result = result.filter(a =>
        (a.display_name || '').toLowerCase().includes(query) ||
        (a.username || '').toLowerCase().includes(query)
      );
    }
    return result.sort((a, b) =>
      (a.display_name || a.username || '').localeCompare(b.display_name || b.username || '')
    );
  }, [artists, searchFilter]);

  // Get videos for a specific artist
  const getArtistVideos = (artistId) => {
    return videos.filter(v => v.artists?.some(a => a.id === artistId));
  };

  // Select artist to view details
  const handleSelectArtist = (artist) => {
    if (selectedArtist?.id === artist.id) {
      setSelectedArtist(null);
      setArtistVideos([]);
    } else {
      setSelectedArtist(artist);
      setArtistVideos(getArtistVideos(artist.id));
    }
  };

  // --- MERGE ---
  const startMerge = (artist) => {
    setDuplicateArtist(artist);
    setKeepArtist(null);
    setMergeSearchQuery('');
    setMergeSearchResults([]);
    setMergeMode(true);
  };

  const handleMergeSearch = async (query) => {
    setMergeSearchQuery(query);
    if (query.trim().length < 2) {
      setMergeSearchResults([]);
      return;
    }
    try {
      const response = await youtubeAdminAPI.searchArtists(query);
      // Filter out the duplicate artist from results
      const results = (response.data || []).filter(a => a.id !== duplicateArtist?.id);
      setMergeSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      // Fall back to local filtering
      const q = query.toLowerCase();
      setMergeSearchResults(
        artists.filter(a =>
          a.id !== duplicateArtist?.id &&
          ((a.display_name || '').toLowerCase().includes(q) ||
           (a.username || '').toLowerCase().includes(q))
        ).slice(0, 10)
      );
    }
  };

  const confirmMerge = () => {
    if (!duplicateArtist || !keepArtist) return;
    setConfirmModal({
      show: true,
      title: 'Confirm Merge',
      message: `This will merge "${duplicateArtist.display_name || duplicateArtist.username}" into "${keepArtist.display_name || keepArtist.username}". All battles from the duplicate will be reassigned to the kept artist, and the duplicate will be deleted. This cannot be undone.`,
      onConfirm: executeMerge,
    });
  };

  const executeMerge = async () => {
    setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
    setMerging(true);
    try {
      // Use any video ID - the endpoint accepts any since it's a global operation
      const anyVideoId = videos[0]?.id || 1;
      const response = await adminVideosAPI.mergeArtists(anyVideoId, {
        duplicate_artist_id: duplicateArtist.id,
        keep_artist_id: keepArtist.id,
      });
      setAlertModal({
        show: true,
        title: 'Merge Successful',
        message: `Merged "${duplicateArtist.display_name || duplicateArtist.username}" into "${keepArtist.display_name || keepArtist.username}". ${response.data.reassigned_count} battle(s) reassigned.`,
        type: 'success',
      });
      setMergeMode(false);
      setDuplicateArtist(null);
      setKeepArtist(null);
      setSelectedArtist(null);
      fetchData();
    } catch (error) {
      console.error('Merge error:', error);
      setAlertModal({
        show: true, title: 'Merge Failed',
        message: error.response?.data?.error || 'Failed to merge artists',
        type: 'error',
      });
    } finally {
      setMerging(false);
    }
  };

  // --- REASSIGN ---
  const startReassign = (video, oldArtist) => {
    setReassignVideo(video);
    setReassignOldArtist(oldArtist);
    setReassignSearchQuery('');
    setReassignSearchResults([]);
  };

  const handleReassignSearch = async (query) => {
    setReassignSearchQuery(query);
    if (query.trim().length < 2) {
      setReassignSearchResults([]);
      return;
    }
    try {
      const response = await youtubeAdminAPI.searchArtists(query);
      const results = (response.data || []).filter(a => a.id !== reassignOldArtist?.id);
      setReassignSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      const q = query.toLowerCase();
      setReassignSearchResults(
        artists.filter(a =>
          a.id !== reassignOldArtist?.id &&
          ((a.display_name || '').toLowerCase().includes(q) ||
           (a.username || '').toLowerCase().includes(q))
        ).slice(0, 10)
      );
    }
  };

  const executeReassign = async (newArtist) => {
    setReassigning(true);
    try {
      await adminVideosAPI.reassignArtist(reassignVideo.id, {
        old_artist_id: reassignOldArtist.id,
        new_artist_id: newArtist.id,
      });
      setAlertModal({
        show: true,
        title: 'Reassign Successful',
        message: `Replaced "${reassignOldArtist.display_name || reassignOldArtist.username}" with "${newArtist.display_name || newArtist.username}" on "${reassignVideo.title}"`,
        type: 'success',
      });
      setReassignVideo(null);
      setReassignOldArtist(null);
      fetchData();
    } catch (error) {
      console.error('Reassign error:', error);
      setAlertModal({
        show: true, title: 'Reassign Failed',
        message: error.response?.data?.error || 'Failed to reassign artist',
        type: 'error',
      });
    } finally {
      setReassigning(false);
    }
  };

  const executeReassignByName = async () => {
    if (!reassignSearchQuery.trim()) return;
    setReassigning(true);
    try {
      await adminVideosAPI.reassignArtist(reassignVideo.id, {
        old_artist_id: reassignOldArtist.id,
        new_artist_name: reassignSearchQuery.trim(),
      });
      setAlertModal({
        show: true,
        title: 'Reassign Successful',
        message: `Replaced "${reassignOldArtist.display_name || reassignOldArtist.username}" with "${reassignSearchQuery.trim()}" on "${reassignVideo.title}"`,
        type: 'success',
      });
      setReassignVideo(null);
      setReassignOldArtist(null);
      fetchData();
    } catch (error) {
      console.error('Reassign error:', error);
      setAlertModal({
        show: true, title: 'Reassign Failed',
        message: error.response?.data?.error || 'Failed to reassign artist',
        type: 'error',
      });
    } finally {
      setReassigning(false);
    }
  };

  // --- CREATE RAPPER ---
  const handleCreateRapper = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const response = await adminArtistsAPI.create(createName.trim());
      setAlertModal({
        show: true,
        title: 'Rapper Created',
        message: `"${response.data.display_name || response.data.username}" is ready.`,
        type: 'success',
      });
      setCreateName('');
      fetchData();
    } catch (error) {
      setAlertModal({
        show: true,
        title: 'Create Failed',
        message: error.response?.data?.error || 'Failed to create rapper',
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  // --- CONVERT USER TO RAPPER ---
  const handleConvertUserSearch = async (query) => {
    setConvertUserSearch(query);
    setConvertTarget(null);
    if (query.trim().length < 2) {
      setConvertUserResults([]);
      return;
    }
    try {
      const response = await adminUsersAPI.search({ search: query, per_page: 10 });
      setConvertUserResults(response.data || []);
    } catch (error) {
      setConvertUserResults([]);
    }
  };

  const handleConvertPlaceholderSearch = async (query) => {
    setConvertPlaceholderSearch(query);
    setConvertPlaceholder(null);
    if (query.trim().length < 2) {
      setConvertPlaceholderResults([]);
      return;
    }
    try {
      const response = await youtubeAdminAPI.searchArtists(query);
      // Endpoint returns { artists: [...] }, not a bare array.
      setConvertPlaceholderResults(response.data.artists || []);
    } catch (error) {
      const q = query.toLowerCase();
      setConvertPlaceholderResults(
        artists.filter(a =>
          (a.display_name || '').toLowerCase().includes(q) ||
          (a.username || '').toLowerCase().includes(q)
        ).slice(0, 10)
      );
    }
  };

  const handleConvertSubmit = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      const response = await adminUsersAPI.promoteToArtist(
        convertTarget.id,
        convertPlaceholder ? convertPlaceholder.id : null
      );
      const data = response.data;
      const parts = [
        `"${data.display_name || data.username}" is now an artist.`,
        data.battles_reassigned > 0 && `${data.battles_reassigned} battle(s) reassigned.`,
        data.votes_repointed > 0 && `${data.votes_repointed} vote(s) repointed.`,
        data.placeholder_deleted && 'Placeholder deleted.',
      ].filter(Boolean);
      setAlertModal({
        show: true,
        title: 'Conversion Complete',
        message: parts.join(' '),
        type: 'success',
      });
      setShowConvertPanel(false);
      setConvertTarget(null);
      setConvertPlaceholder(null);
      setConvertUserSearch('');
      setConvertPlaceholderSearch('');
      fetchData();
    } catch (error) {
      setAlertModal({
        show: true,
        title: 'Conversion Failed',
        message: error.response?.data?.error || 'Failed to convert user',
        type: 'error',
      });
    } finally {
      setConverting(false);
    }
  };

  // --- AVATAR UPLOAD (admin uploads for any artist) ---
  const triggerAvatarUpload = (artistId, e) => {
    e.stopPropagation();
    setPendingAvatarArtistId(artistId);
    avatarFileInputRef.current && avatarFileInputRef.current.click();
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !pendingAvatarArtistId) return;
    const artistId = pendingAvatarArtistId;
    setPendingAvatarArtistId(null);
    setAvatarUploadingId(artistId);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      await adminUsersAPI.uploadAvatar(artistId, dataUrl);
      setAlertModal({
        show: true,
        title: 'Avatar Updated',
        message: 'Avatar uploaded successfully.',
        type: 'success',
      });
      fetchData();
    } catch (err) {
      const msgs = err.response?.data?.errors;
      setAlertModal({
        show: true,
        title: 'Upload Failed',
        message: msgs ? msgs.join(', ') : 'Failed to upload avatar.',
        type: 'error',
      });
    } finally {
      setAvatarUploadingId(null);
    }
  };

  // --- RENDER ---

  if (authLoading) {
    return (
      <div className="min-h-screen bg-battle-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-battle-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Artist Management
          </h1>
          <p className="text-gray-400">
            Manage artists, merge duplicates, and reassign battles
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {artists.length} artists total
          </p>
        </div>

        {/* Admin Actions: Create Rapper + Convert User */}
        <div className="mb-6 flex flex-wrap gap-4">
          {/* Create Rapper */}
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateRapper()}
              placeholder="New rapper name..."
              className="px-4 py-2 bg-battle-gray border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors w-56"
            />
            <button
              onClick={handleCreateRapper}
              disabled={!createName.trim() || creating}
              className="px-4 py-2 bg-brand-yellow hover:bg-yellow-400 disabled:bg-gray-700 disabled:text-gray-500 text-black font-bold rounded transition-colors whitespace-nowrap"
            >
              {creating ? 'Creating...' : 'Create Rapper'}
            </button>
          </div>

          {/* Convert User to Rapper */}
          <button
            onClick={() => setShowConvertPanel(!showConvertPanel)}
            className="px-4 py-2 bg-army/60 hover:bg-army text-white font-bold rounded transition-colors whitespace-nowrap"
          >
            {showConvertPanel ? 'Hide Converter' : 'Convert User to Rapper'}
          </button>
        </div>

        {/* Convert User to Rapper Panel */}
        {showConvertPanel && (
          <div className="mb-6 bg-battle-gray border border-army/40 rounded-lg p-5">
            <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-wide">Convert User to Rapper</h3>
            <p className="text-gray-400 text-sm mb-4">
              Find a real user (has a real Google account), optionally select a placeholder artist to absorb,
              then promote. The real user survives; the placeholder gets absorbed and deleted.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Step 1: Find the real user */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Step 1: Find the real user (search by email or username)
                </label>
                <input
                  type="text"
                  value={convertUserSearch}
                  onChange={(e) => handleConvertUserSearch(e.target.value)}
                  placeholder="Email or username..."
                  className="w-full px-4 py-2 bg-battle-gray-light border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors mb-2"
                />
                {convertUserResults.length > 0 && !convertTarget && (
                  <div className="max-h-40 overflow-y-auto border border-gray-700 rounded-lg">
                    {convertUserResults.map(u => (
                      <button
                        key={u.id}
                        onClick={() => { setConvertTarget(u); setConvertUserResults([]); }}
                        className="w-full text-left px-4 py-2 hover:bg-battle-gray-light text-gray-300 hover:text-white transition-colors"
                      >
                        <div className="font-medium">{u.display_name || u.username}</div>
                        <div className="text-gray-500 text-xs">{u.email} ({u.role})</div>
                      </button>
                    ))}
                  </div>
                )}
                {convertTarget && (
                  <div className="bg-brand-yellow/10 border border-brand-yellow/40 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white font-bold">{convertTarget.display_name || convertTarget.username}</div>
                      <div className="text-gray-400 text-xs">{convertTarget.email} ({convertTarget.role})</div>
                    </div>
                    <button
                      onClick={() => { setConvertTarget(null); setConvertUserSearch(''); }}
                      className="text-gray-400 hover:text-white text-sm ml-3"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Step 2: Optionally pick a placeholder to absorb */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Step 2 (optional): Placeholder artist to absorb
                </label>
                <input
                  type="text"
                  value={convertPlaceholderSearch}
                  onChange={(e) => handleConvertPlaceholderSearch(e.target.value)}
                  placeholder="Search placeholder artist..."
                  className="w-full px-4 py-2 bg-battle-gray-light border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors mb-2"
                />
                {convertPlaceholderResults.length > 0 && !convertPlaceholder && (
                  <div className="max-h-40 overflow-y-auto border border-gray-700 rounded-lg">
                    {convertPlaceholderResults.map(a => (
                      <button
                        key={a.id}
                        onClick={() => { setConvertPlaceholder(a); setConvertPlaceholderResults([]); }}
                        className="w-full text-left px-4 py-2 hover:bg-battle-gray-light text-gray-300 hover:text-white transition-colors"
                      >
                        <div className="font-medium">{a.display_name || a.username}</div>
                        {a.display_name && a.username && (
                          <div className="text-gray-500 text-xs">@{a.username}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {convertPlaceholder && (
                  <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white font-bold">{convertPlaceholder.display_name || convertPlaceholder.username}</div>
                      <div className="text-gray-400 text-xs">Will be absorbed and deleted</div>
                    </div>
                    <button
                      onClick={() => { setConvertPlaceholder(null); setConvertPlaceholderSearch(''); }}
                      className="text-gray-400 hover:text-white text-sm ml-3"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConvertPanel(false);
                  setConvertTarget(null);
                  setConvertPlaceholder(null);
                  setConvertUserSearch('');
                  setConvertPlaceholderSearch('');
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertSubmit}
                disabled={!convertTarget || converting}
                className="px-4 py-2 bg-brand-yellow hover:bg-yellow-400 disabled:bg-gray-700 disabled:text-gray-500 text-black font-bold rounded transition-colors"
              >
                {converting
                  ? 'Converting...'
                  : convertPlaceholder
                    ? 'Promote + Absorb Placeholder'
                    : 'Promote to Rapper'}
              </button>
            </div>
          </div>
        )}

        {/* Search Filter */}
        <div className="mb-6">
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Filter artists by name..."
            className="w-full md:w-96 px-4 py-3 bg-battle-gray border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors"
          />
        </div>

        {/* Hidden file input for admin avatar upload — one shared input, triggered per artist */}
        <input
          ref={avatarFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarFileChange}
        />

        {/* Main Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-yellow"></div>
              <div className="text-white text-xl">Loading artists...</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Artist List */}
            <div>
              <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-wide">
                Artists ({filteredArtists.length})
              </h2>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                {filteredArtists.map(artist => {
                  const artistAvatarSrc = artist.has_avatar
                    ? `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api/v1'}/users/${artist.id}/avatar`
                    : artist.avatar_url || null;
                  return (
                  <div
                    key={artist.id}
                    onClick={() => handleSelectArtist(artist)}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedArtist?.id === artist.id
                        ? 'bg-brand-yellow/10 border border-brand-yellow/40'
                        : 'bg-battle-gray-light hover:bg-battle-gray-light/80 border border-transparent'
                    }`}
                  >
                    {/* Artist avatar thumbnail */}
                    <div className="flex-shrink-0 mr-3">
                      {artistAvatarSrc ? (
                        <img
                          src={artistAvatarSrc}
                          alt={artist.display_name || artist.username}
                          className="w-9 h-9 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div
                        className="w-9 h-9 rounded-full bg-army/30 flex items-center justify-center text-white font-bold text-sm"
                        style={{ display: artistAvatarSrc ? 'none' : 'flex' }}
                      >
                        {(artist.display_name || artist.username || '?').charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">
                        {artist.display_name || artist.username}
                      </div>
                      {artist.display_name && artist.username && (
                        <div className="text-gray-500 text-sm truncate">
                          @{artist.username}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className="text-gray-400 text-sm whitespace-nowrap">
                        {artist.video_count} {artist.video_count === 1 ? 'battle' : 'battles'}
                      </span>
                      <button
                        onClick={(e) => triggerAvatarUpload(artist.id, e)}
                        disabled={avatarUploadingId === artist.id}
                        className="px-2 py-1 text-xs bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 rounded transition-colors disabled:opacity-50"
                        title="Upload avatar for this artist"
                      >
                        {avatarUploadingId === artist.id ? '...' : 'Avatar'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startMerge(artist);
                        }}
                        className="px-3 py-1 text-xs bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/40 rounded transition-colors"
                        title="Merge this artist into another"
                      >
                        Merge
                      </button>
                    </div>
                  </div>
                  );
                })}
                {filteredArtists.length === 0 && (
                  <div className="text-gray-500 text-center py-8">
                    No artists match your search
                  </div>
                )}
              </div>
            </div>

            {/* Detail Panel */}
            <div>
              {selectedArtist ? (
                <div>
                  <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-wide">
                    {selectedArtist.display_name || selectedArtist.username} - Battles
                  </h2>
                  {artistVideos.length === 0 ? (
                    <div className="text-gray-500 text-center py-8 bg-battle-gray-light rounded-lg">
                      No battles found for this artist
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                      {artistVideos.map(video => (
                        <div
                          key={video.id}
                          className="bg-battle-gray-light rounded-lg p-4 border border-gray-800"
                        >
                          <div className="flex items-start gap-3">
                            {video.thumbnail_url && (
                              <img
                                src={video.thumbnail_url}
                                alt={video.title}
                                className="w-24 h-16 object-cover rounded flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-white font-medium text-sm truncate">
                                {video.title}
                              </div>
                              <div className="text-gray-400 text-xs mt-1">
                                Artists: {video.artists?.map(a => a.display_name || a.username).join(' vs ')}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {video.artists?.map(a => (
                                  <button
                                    key={a.id}
                                    onClick={() => startReassign(video, a)}
                                    className="px-2 py-1 text-xs bg-battle-gray hover:bg-gray-700 text-gray-300 hover:text-white rounded transition-colors border border-gray-700"
                                    title={`Reassign ${a.display_name || a.username} on this video`}
                                  >
                                    Swap: {a.display_name || a.username}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-battle-gray-light rounded-lg border border-gray-800">
                  <p className="text-gray-500">Select an artist to view their battles</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Merge Modal */}
        {mergeMode && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-battle-gray border border-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-2">Merge Artist</h3>
              <p className="text-gray-400 text-sm mb-4">
                Merging <span className="text-battle-red font-semibold">{duplicateArtist?.display_name || duplicateArtist?.username}</span> (duplicate) into another artist.
                All battles will be reassigned and the duplicate will be deleted.
              </p>

              {/* Search for keep artist */}
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Search for the correct artist to keep:
              </label>
              <input
                type="text"
                value={mergeSearchQuery}
                onChange={(e) => handleMergeSearch(e.target.value)}
                placeholder="Type artist name..."
                className="w-full px-4 py-3 bg-battle-gray-light border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors mb-3"
                autoFocus
              />

              {/* Search Results */}
              {mergeSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-700 rounded-lg mb-4">
                  {mergeSearchResults.map(a => (
                    <button
                      key={a.id}
                      onClick={() => {
                        setKeepArtist(a);
                        setMergeSearchQuery(a.display_name || a.username);
                        setMergeSearchResults([]);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-battle-gray-light transition-colors ${
                        keepArtist?.id === a.id ? 'bg-brand-yellow/10 text-white' : 'text-gray-300'
                      }`}
                    >
                      <div className="font-medium">{a.display_name || a.username}</div>
                      {a.display_name && a.username && (
                        <div className="text-gray-500 text-xs">@{a.username}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Selected keep artist */}
              {keepArtist && (
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-3 mb-4">
                  <div className="text-green-400 text-sm font-medium">Keep artist:</div>
                  <div className="text-white font-bold">{keepArtist.display_name || keepArtist.username}</div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setMergeMode(false);
                    setDuplicateArtist(null);
                    setKeepArtist(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMerge}
                  disabled={!keepArtist || merging}
                  className="px-4 py-2 bg-battle-red hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                >
                  {merging ? 'Merging...' : 'Merge Artists'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reassign Modal */}
        {reassignVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-battle-gray border border-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-2">Reassign Artist</h3>
              <p className="text-gray-400 text-sm mb-1">
                Video: <span className="text-white">{reassignVideo.title}</span>
              </p>
              <p className="text-gray-400 text-sm mb-4">
                Replacing: <span className="text-battle-red font-semibold">{reassignOldArtist?.display_name || reassignOldArtist?.username}</span>
              </p>

              {/* Search for replacement */}
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Search for the replacement artist (or type a new name):
              </label>
              <input
                type="text"
                value={reassignSearchQuery}
                onChange={(e) => handleReassignSearch(e.target.value)}
                placeholder="Type artist name..."
                className="w-full px-4 py-3 bg-battle-gray-light border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors mb-3"
                autoFocus
              />

              {/* Search Results */}
              {reassignSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-700 rounded-lg mb-4">
                  {reassignSearchResults.map(a => (
                    <button
                      key={a.id}
                      onClick={() => executeReassign(a)}
                      disabled={reassigning}
                      className="w-full text-left px-4 py-2 hover:bg-battle-gray-light text-gray-300 hover:text-white transition-colors"
                    >
                      <div className="font-medium">{a.display_name || a.username}</div>
                      {a.display_name && a.username && (
                        <div className="text-gray-500 text-xs">@{a.username}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setReassignVideo(null);
                    setReassignOldArtist(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeReassignByName}
                  disabled={!reassignSearchQuery.trim() || reassigning}
                  className="px-4 py-2 bg-battle-red hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                >
                  {reassigning ? 'Reassigning...' : 'Reassign by Name'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <ConfirmModal
          show={confirmModal.show}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
          confirmText="Merge"
          confirmButtonStyle="bg-battle-red hover:bg-red-700"
        />

        <AlertModal
          show={alertModal.show}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
        />
      </div>
    </div>
  );
};

export default ArtistManagement;
